import { useAuth } from '@lingugeek/state-management';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import LoadingSpinner from '@/components/LoadingSpinner';
import { isActiveUser } from '@lingugeek/schemas';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, isLoading, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pathname = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Check if current page is a revision page
  const isRevisionPage = pathname.pathname?.startsWith('/revision');

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <LoadingSpinner size='lg' className='mb-4' />
          <p className='text-brand-navy'>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect to login via useAuth hook
  }

  return (
    <div className='min-h-screen'>
      <DashboardNavbar
        user={user}
        onLogout={handleLogout}
        onMenuToggle={() => setSidebarOpen(o => !o)}
      />
      <div className='flex h-[calc(100vh-64px)]'>
        {isActiveUser(user) && (
          <DashboardSidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        )}
        <main
          className={`${!isActiveUser(user) ? 'w-full' : 'flex-1'} p-6 overflow-y-auto`}
        >
          {isRevisionPage ? (
            <div className='w-full'>{children}</div>
          ) : (
            <div className='max-w-4xl mx-auto'>{children}</div>
          )}
        </main>
      </div>
    </div>
  );
}
